import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getReceiptCategories, getActiveReceiptCategories, getReceiptConcepts,
  getReceipts, getReceiptsByMinister, getReceiptById, getReceiptBySource, getIssuedReceiptSourceIds, issueReceipt, voidReceipt,
} from '../data/receiptsRepository';
import type { ReceiptFilters, IssueReceiptInput } from '../types';

const RECEIPTS_KEY   = ['receipts'];
const CATEGORIES_KEY = ['receipt_categories'];
const CONCEPTS_KEY   = ['receipt_concepts'];

export function useReceiptCategories() {
  return useQuery({ queryKey: CATEGORIES_KEY, queryFn: getReceiptCategories });
}

export function useActiveReceiptCategories() {
  return useQuery({ queryKey: [...CATEGORIES_KEY, 'active'], queryFn: getActiveReceiptCategories });
}

export function useReceiptConcepts() {
  return useQuery({ queryKey: CONCEPTS_KEY, queryFn: getReceiptConcepts });
}

export function useReceiptsList(filters: ReceiptFilters) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, filters],
    queryFn: () => getReceipts(filters),
    staleTime: 60_000,
  });
}

/** A minister's receipt history; one query, fetched only when their Recibos tab is opened. */
export function useMinisterReceipts(ministerId: string | undefined) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, 'minister', ministerId],
    queryFn: () => getReceiptsByMinister(ministerId as string),
    enabled: !!ministerId,
  });
}

export function useReceipt(id: string | undefined) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, id],
    queryFn: () => getReceiptById(id as string),
    enabled: !!id,
  });
}

/** Looks up a receipt already issued for a source record (e.g. a tithe). */
export function useReceiptBySource(sourceType: string | undefined, sourceId: string | undefined) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, 'source', sourceType, sourceId],
    queryFn: () => getReceiptBySource(sourceType as string, sourceId as string),
    enabled: !!sourceType && !!sourceId,
  });
}

/** Ids of source records (e.g. tithes) with an issued receipt. One cached query for all years. */
export function useIssuedReceiptSourceIds(sourceType: string) {
  return useQuery({
    queryKey: [...RECEIPTS_KEY, 'issued-sources', sourceType],
    queryFn: () => getIssuedReceiptSourceIds(sourceType),
    staleTime: 5 * 60_000,
  });
}

export function useReceiptMutations() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: RECEIPTS_KEY });

  const issue = useMutation({
    mutationFn: ({ input, totalInWords }: { input: IssueReceiptInput; totalInWords: string }) =>
      issueReceipt(input, totalInWords),
    onSuccess: invalidate,
  });

  const voidOne = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => voidReceipt(id, reason),
    onSuccess: invalidate,
  });

  return { issue, voidOne };
}
